import { useEffect, useRef, useState } from 'react';
import './style.scss';
import FocusTrap from '../../Shared/FocusTrap';
import { TranslationService } from '../../../services/translation.service';
import SelectFormGroup, { type ISelectFormGroupState } from '../../Shared/SelectFormGroup';
import ValidationInfo from '../../../validation/validation-info';
import useFetch, { Status } from '../../../hooks/fetch.hook';
import MunicipalityApiModel from '../models/municipality.api-model';
import type IValidatable from '../../Shared/validatable.interface';
import { ValidationHelper, type ValidatableComponent } from '../../../helpers/validation.helper';
import { ClientSideStorageItems, ClientSideStorageService } from '../../../services/client-side-storage.service';
import { MunicipalitySelectorModalHelper } from '../../../../components/municipality-selector-modal/municipality-selector-modal.helper';

function MunicipalitySelectorModal() {
  const selectedMunicipalityFromCookies = MunicipalitySelectorModalHelper.getSelectedMunicipalityFromCookies();

  // === STATE ===
  const [visible, setVisible] = useState(() => !selectedMunicipalityFromCookies && ClientSideStorageService.isConsentStatusSet());
  const [getParticipatingMunicipalities] = useFetch<MunicipalityApiModel[]>('/api/paegnaben/municipality-selector-modal/participating-municipalities');
  const [selectedMunicipalityState, setSelectedMunicipalityState] = useState<ISelectFormGroupState>({
    value: selectedMunicipalityFromCookies && selectedMunicipalityFromCookies.gid !== MunicipalitySelectorModalHelper.NO_SELECTED_MUNICIPALITY_GID ? selectedMunicipalityFromCookies.gid : '',
    validationInfo: new ValidationInfo(),
  });

  // === REFS ===
  const selectedMunicipalityRef = useRef<IValidatable>(null!);

  function getValidatableComponents(): ValidatableComponent[] {
    return [{ component: selectedMunicipalityRef.current, validationInfo: selectedMunicipalityState.validationInfo, setState: setSelectedMunicipalityState }];
  }

  function updateVisibilityFromCookiesAndConsent() {
    const selectedMunicipality = MunicipalitySelectorModalHelper.getSelectedMunicipalityFromCookies();
    if (selectedMunicipality) {
      if (selectedMunicipality.gid === MunicipalitySelectorModalHelper.NO_SELECTED_MUNICIPALITY_GID) {
        setVisible(false);
      } else {
        setSelectedMunicipalityState({ value: selectedMunicipality.gid, validationInfo: new ValidationInfo() });
      }
      return;
    }

    setVisible(ClientSideStorageService.isConsentStatusSet());
  }

  // == EFFECTS ===

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }
      if (event.data.type === MunicipalitySelectorModalHelper.OPEN_EVENT_NAME) {
        if (!ClientSideStorageService.isConsentStatusSet()) {
          document.dispatchEvent(new CustomEvent('show-cookie-consent-modal'));
          return;
        }

        setVisible(true);
      }
    };

    const handleConsentStatusApproved = () => {
      updateVisibilityFromCookiesAndConsent();
    };

    window.addEventListener('message', handleMessage);
    document.addEventListener('consent-status-approved', handleConsentStatusApproved);

    return () => {
      window.removeEventListener('message', handleMessage);
      document.removeEventListener('consent-status-approved', handleConsentStatusApproved);
    };
  }, []);

  // === VARIABLES ===
  const isModalVisible = visible && getParticipatingMunicipalities.status !== Status.failed;
  const participatingMunicipalities = getParticipatingMunicipalities?.data?.map(MunicipalityApiModel.mapToBusinessModel) ?? [];

  // === EVENT HANDLERS ===
  function handleOnClose() {
    // NV-6602: If no municipality is selected, set a cookie with a dummy municipality to prevent the modal from showing again
    if (!selectedMunicipalityState.value) {
      ClientSideStorageService.setCookie(
        ClientSideStorageItems.Cookie.SelectedMunicipality,
        JSON.stringify({ gid: MunicipalitySelectorModalHelper.NO_SELECTED_MUNICIPALITY_GID, name: MunicipalitySelectorModalHelper.NO_SELECTED_MUNICIPALITY_NAME }),
        365,
      );
    }
    setVisible(false);
  }

  function handleOnSaveClick(ev: React.MouseEvent<HTMLButtonElement>) {
    ev.preventDefault();
    const [isFormValid] = ValidationHelper.checkValidity(getValidatableComponents());
    if (!isFormValid) {
      return;
    }

    const selectedMunicipality = participatingMunicipalities.find((x) => x.gid === selectedMunicipalityState.value);
    if (!selectedMunicipality) {
      return;
    }

    ClientSideStorageService.setCookie(ClientSideStorageItems.Cookie.SelectedMunicipality, JSON.stringify({ gid: selectedMunicipality.gid, name: selectedMunicipality.name }), 365);
    window.location.reload();
  }

  return (
    <>
      {isModalVisible && (
        <FocusTrap isVisible={true}>
          <div className="municipality-selector-modal modal d-block fade show" aria-labelledby="municipality-selector-modal-title" aria-modal="true" role="dialog">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <div id="municipality-selector-modal-title" className="modal-title h3">
                    Välkommen
                  </div>
                  <button type="button" className="btn-close" aria-label={TranslationService.get('common.close')} onClick={handleOnClose}></button>
                </div>
                <div className="modal-body pt-0">
                  <p>Se nyheter om På egna ben i din kommun</p>
                  <form>
                    <SelectFormGroup
                      id="municipality-selector"
                      ref={selectedMunicipalityRef}
                      labelText="Välj kommun"
                      hideLabel={true}
                      placeholder="Välj kommun"
                      required={true}
                      options={participatingMunicipalities.sort().map((x) => ({ text: x.name, value: x.gid }))}
                      state={selectedMunicipalityState}
                      onChange={(x) => setSelectedMunicipalityState({ value: x, validationInfo: new ValidationInfo() })}
                    ></SelectFormGroup>
                    <div className="d-grid gap-0">
                      <button type="submit" className="btn btn-primary" onClick={handleOnSaveClick}>
                        Spara
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </FocusTrap>
      )}
      {isModalVisible && <div className="modal-backdrop municipality-selector-modal__backdrop fade show"></div>}
    </>
  );
}

export default MunicipalitySelectorModal;
